// src/components/listing/specialized/ElectronicsForm.jsx
import React from 'react';
import Input from '../../../common/Input';
import Dropdown from '../../../common/Dropdown';

const ElectronicsForm = ({ form, errors, submitting, onFieldChange, categoryId }) => {
  
  // Determine electronics type based on category ID
  const getElectronicsType = () => {
    if (!categoryId) return 'general';
    
    const categoryNum = parseInt(categoryId);
    // console.log('📱 Electronics category ID:', categoryNum);
    
    switch(categoryNum) {
      case 12: return 'computers'; // Computers & Laptops
      case 19: return 'phones'; // Mobile Phones
      case 20: return 'tablets'; // Tablets
      case 22: return 'wearables'; // Smartwatches
      case 13: return 'tv_audio'; // TVs & Audio Systems
      case 14: return 'cameras'; // Cameras & Drones
      case 15: return 'gaming'; // Gaming // consoles
      default: return 'general';
    }
  };

  const electronicsType = getElectronicsType();
  // console.log('🎯 Electronics type:', electronicsType);

  const getBrandPlaceholder = () => {
    switch(electronicsType) {
      case 'phones': return 'e.g., Apple, Samsung, Google';
      case 'computers': return 'e.g., Apple, Dell, HP, Lenovo';
      case 'tablets': return 'e.g., Apple, Samsung, Microsoft';
      case 'wearables': return 'e.g., Apple, Samsung, Fitbit, Garmin';
      case 'tv_audio': return 'e.g., Samsung, LG, Sony, Bose';
      case 'cameras': return 'e.g., Canon, Nikon, Sony, DJI';
      case 'gaming': return 'e.g., Sony, Microsoft, Nintendo';
      default: return 'e.g., Brand name';
    }
  };

  const getModelPlaceholder = () => {
    switch(electronicsType) {
      case 'phones': return 'e.g., iPhone 15, Galaxy S24';
      case 'computers': return 'e.g., MacBook Pro, ThinkPad X1';
      case 'tablets': return 'e.g., iPad Pro, Galaxy Tab S9';
      case 'wearables': return 'e.g., Apple Watch, Galaxy Watch';
      case 'tv_audio': return 'e.g., OLED TV, Soundbar 500';
      case 'cameras': return 'e.g., EOS R5, Alpha 7IV';
      case 'gaming': return 'e.g., PlayStation 5, Xbox Series X';
      default: return 'e.g., Model name';
    }
  };

  const renderSimpleItem = (item) => (
    <div className="p-3 hover:bg-gray-50 cursor-pointer">
      <span className="text-gray-900">{item.name || item.label}</span>
    </div>
  );

  // Common fields for all electronics
  const renderCommonFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Brand *
          </label>
          <Input
            type="text"
            placeholder={getBrandPlaceholder()}
            value={form.brand || ''}
            onChange={(e) => onFieldChange('brand', e.target.value)}
            error={errors.brand}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Model *
          </label>
          <Input
            type="text"
            placeholder={getModelPlaceholder()}
            value={form.model || ''}
            onChange={(e) => onFieldChange('model', e.target.value)}
            error={errors.model}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Color
        </label>
        <Input
          type="text"
          placeholder="e.g., Black, Silver, Space Gray"
          value={form.color || ''}
          onChange={(e) => onFieldChange('color', e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  // Fields specific to Mobile Phones
  const renderPhoneFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Storage
          </label>
          <Input
            type="text"
            placeholder="e.g., 128GB, 256GB"
            value={form.storage_capacity || ''}
            onChange={(e) => onFieldChange('storage_capacity', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            RAM
          </label>
          <Input
            type="text"
            placeholder="e.g., 6GB, 8GB"
            value={form.ram || ''}
            onChange={(e) => onFieldChange('ram', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screen Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 6.1 inches"
            value={form.screen_size || ''}
            onChange={(e) => onFieldChange('screen_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Battery Health
          </label>
          <Input
            type="text"
            placeholder="e.g., 95%, Good"
            value={form.battery_health || ''}
            onChange={(e) => onFieldChange('battery_health', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Network Type
        </label>
        <Dropdown
          value={form.network_type || ''}
          onValueChange={(value) => onFieldChange('network_type', value)}
          items={[
            { id: '4g', name: '4G', value: '4g' },
            { id: '5g', name: '5G', value: '5g' },
            { id: 'dual_4g', name: 'Dual 4G', value: 'dual_4g' },
            { id: 'dual_5g', name: 'Dual 5G', value: 'dual_5g' },
          ]}
          disabled={submitting}
          renderItem={renderSimpleItem}
        />
      </div>
    </>
  );

  // Fields specific to Computers & Laptops
  const renderComputerFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Storage
          </label>
          <Input
            type="text"
            placeholder="e.g., 512GB SSD, 1TB HDD"
            value={form.storage_capacity || ''}
            onChange={(e) => onFieldChange('storage_capacity', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            RAM
          </label>
          <Input
            type="text"
            placeholder="e.g., 8GB, 16GB DDR4"
            value={form.ram || ''}
            onChange={(e) => onFieldChange('ram', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Processor
        </label>
        <Input
          type="text"
          placeholder="e.g., Intel i7, Apple M2, AMD Ryzen 7"
          value={form.processor || ''}
          onChange={(e) => onFieldChange('processor', e.target.value)}
          disabled={submitting}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screen Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 14 inches, 16 inches"
            value={form.screen_size || ''}
            onChange={(e) => onFieldChange('screen_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Operating System
          </label>
          <Dropdown
            value={form.operating_system || ''}
            onValueChange={(value) => onFieldChange('operating_system', value)}
            items={[
              { id: 'windows', name: 'Windows', value: 'windows' },
              { id: 'macos', name: 'macOS', value: 'macos' },
              { id: 'linux', name: 'Linux', value: 'linux' },
              { id: 'chromeos', name: 'Chrome OS', value: 'chromeos' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>
    </>
  );

  // Fields specific to Cameras & Drones
  const renderCameraFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Sensor Size
          </label>
          <Input
            type="text"
            placeholder="e.g., Full Frame, APS-C"
            value={form.sensor_size || ''}
            onChange={(e) => onFieldChange('sensor_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Megapixels
          </label>
          <Input
            type="text"
            placeholder="e.g., 24MP, 45MP"
            value={form.megapixels || ''}
            onChange={(e) => onFieldChange('megapixels', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Lens Type
        </label>
        <Input
          type="text"
          placeholder="e.g., Kit Lens, Prime Lens"
          value={form.lens_type || ''}
          onChange={(e) => onFieldChange('lens_type', e.target.value)}
          disabled={submitting}
        />
      </div>
    </>
  );

  // Fields specific to TVs & Audio Systems
  const renderTvAudioFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screen Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 55 inches, 65 inches"
            value={form.screen_size || ''}
            onChange={(e) => onFieldChange('screen_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Resolution
          </label>
          <Dropdown
            value={form.resolution || ''}
            onValueChange={(value) => onFieldChange('resolution', value)}
            items={[
              { id: 'hd', name: 'HD', value: 'hd' },
              { id: 'full_hd', name: 'Full HD', value: 'full_hd' },
              { id: '4k', name: '4K UHD', value: '4k' },
              { id: '8k', name: '8K', value: '8k' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>
    </>
  );

  // Fields specific to Gaming // consoles
  const renderGamingFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Storage
          </label>
          <Input
            type="text"
            placeholder="e.g., 1TB SSD, 825GB"
            value={form.storage_capacity || ''}
            onChange={(e) => onFieldChange('storage_capacity', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Edition
          </label>
          <Input
            type="text"
            placeholder="e.g., Standard, Digital Edition"
            value={form.edition || ''}
            onChange={(e) => onFieldChange('edition', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </>
  );

  // Fields specific to Tablets
  const renderTabletFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Storage
          </label>
          <Input
            type="text"
            placeholder="e.g., 128GB, 256GB"
            value={form.storage_capacity || ''}
            onChange={(e) => onFieldChange('storage_capacity', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            RAM
          </label>
          <Input
            type="text"
            placeholder="e.g., 4GB, 8GB"
            value={form.ram || ''}
            onChange={(e) => onFieldChange('ram', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screen Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 11 inches, 12.9 inches"
            value={form.screen_size || ''}
            onChange={(e) => onFieldChange('screen_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cellular Support
          </label>
          <Dropdown
            value={form.cellular_support || 'wifi_only'}
            onValueChange={(value) => onFieldChange('cellular_support', value)}
            items={[
              { id: 'wifi_only', name: 'Wi-Fi Only', value: 'wifi_only' },
              { id: 'wifi_cellular', name: 'Wi-Fi + Cellular', value: 'wifi_cellular' },
            ]}
            disabled={submitting}
            renderItem={renderSimpleItem}
          />
        </div>
      </div>
    </>
  );

  // Fields specific to Wearables (Smartwatches)
  const renderWearableFields = () => (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Screen Size
          </label>
          <Input
            type="text"
            placeholder="e.g., 1.9 inches, 45mm"
            value={form.screen_size || ''}
            onChange={(e) => onFieldChange('screen_size', e.target.value)}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Battery Life
          </label>
          <Input
            type="text"
            placeholder="e.g., 18 hours, 2 days"
            value={form.battery_life || ''}
            onChange={(e) => onFieldChange('battery_life', e.target.value)}
            disabled={submitting}
          />
        </div>
      </div>
    </>
  );

  return (
    <div>
      <h4 className="text-lg font-semibold text-gray-900 mb-4">
        {electronicsType === 'phones' ? 'Mobile Phone Details' :
         electronicsType === 'computers' ? 'Computer & Laptop Details' :
         electronicsType === 'tablets' ? 'Tablet Details' :
         electronicsType === 'wearables' ? 'Smartwatch Details' :
         electronicsType === 'tv_audio' ? 'TV & Audio System Details' :
         electronicsType === 'cameras' ? 'Camera & Drone Details' :
         electronicsType === 'gaming' ? 'Gaming // console Details' :
         'Electronic Details'}
      </h4>
      
      {/* Common fields */}
      {renderCommonFields()}

      {/* Type-specific fields */}
      {electronicsType === 'phones' && renderPhoneFields()}
      {electronicsType === 'computers' && renderComputerFields()}
      {electronicsType === 'tablets' && renderTabletFields()}
      {electronicsType === 'wearables' && renderWearableFields()}
      {electronicsType === 'tv_audio' && renderTvAudioFields()}
      {electronicsType === 'cameras' && renderCameraFields()}
      {electronicsType === 'gaming' && renderGamingFields()}
    </div>
  );
};

export default ElectronicsForm;